import _ from "lodash";
import React, { useState, useEffect } from "react";
import classNames from "classnames";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import UIModal from "UIComponents/UIModal";
import CustomProgressSteps from "UIComponents/CustomProgressSteps";
import I18nHOC from "UIComponents/I18nHOC";
import classes from "./AssessmentImportModal.scss";
import TopHeader from "AppComponents/TopHeaderNavBar";
import TruncateWithTooltip from "UIComponents/TruncateWithTooltip";
import { getModalHeaderText, getStepListMemoized } from "./Utils";
import {
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_UBD,
} from "Constants/stringConstants";
import UnitSelection from "./UnitSelection";
import LESelection from "./LESelection";
import { Button } from "@toddle-edu/ds-web";
import {
  setToastMsg,
  setGlobalTrayEnabledState,
} from "Login/modules/LoginModule";
import PropTypes from "prop-types";
import { hideIntercom, showIntercom } from "modules/Services";

function AssessmentImportModal(props) {
  const {
    onClose,
    steps,
    t,
    currentSelectedStep,
    portalType,
    updateUserInfoPortalType,
    updateItems,
    onClickAdd,
    setToastMsg,
    modalType,
    header,
    groupType,
    importButtonText,
    setGlobalTrayEnabledState,
    hideIntercom,
    showIntercom,
    testId = "unitPlan",
    unitPlanId = null,
    isMultiSelect = false,
    courseName,
  } = props;

  useEffect(() => {
    updateUserInfoPortalType({ portalType });
  }, [portalType]);

  useEffect(() => {
    setGlobalTrayEnabledState(false); // For Hiding the tray on Component Mounted
    hideIntercom();
    return () => {
      setGlobalTrayEnabledState(true); // For showing the tray on Component Unmounted.
      showIntercom();
    };
  }, []);

  const [selectedStep, setSelectedStep] = useState(currentSelectedStep);
  const [selectedItems, setSelectedItems] = useState({});
  const [unitSelectionFilters, setUnitSelectionFilters] = useState({});

  const onProgressStepClick = ({ step }) => {
    if (
      selectedStep == "unitSelection" &&
      _.isEmpty(_.get(selectedItems, "unitPlan", null))
    ) {
      setToastMsg({
        msg: "toastMsgs:please_select_unit_to_import",
        type: "warning",
        position: "toast-bottom-left",
      });
      return;
    }
    setSelectedStep(step);
  };

  const updateSelectedUnitPlan = ({ unitPlan }) => {
    if (_.get(selectedItems, "unitPlan.id") != unitPlan.id) {
      setSelectedItems({
        unitPlan,
        selectedAssessmentIds: [],
      });
    }
  };

  const updateUnitSelectionFilters = params => {
    setUnitSelectionFilters(prevUnitSelectionFilters => {
      return {
        ...prevUnitSelectionFilters,
        ...params,
      };
    });
  };

  const renderCurrentStep = () => {
    switch (selectedStep) {
      case "unitSelection": {
        return (
          <UnitSelection
            portalType={portalType}
            updateUnitPlan={updateSelectedUnitPlan}
            selectedItems={selectedItems}
            filterList={_.get(
              steps[_.findIndex(steps, { id: selectedStep })],
              "filters",
              []
            )}
            updateUnitSelectionFilters={updateUnitSelectionFilters}
            unitSelectionFilters={unitSelectionFilters}
            modalType={modalType}
            testId="unitPlans-unitSelection"
            excludedUnitPlanIds={unitPlanId ? [unitPlanId] : []}
          />
        );
      }
      case "LeSelection": {
        return (
          <div className={classes.LeSelection}>
            <LESelection
              portalType={portalType}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              updateItems={updateItems}
              groupType={groupType}
              testId="unitPlans-leSelection"
              isMultiSelect={isMultiSelect}
            />
          </div>
        );
      }
    }
  };

  const onRightButtonClick = () => {
    if (_.findIndex(steps, { id: selectedStep }) == _.size(steps) - 1) {
      const hasNoSelectedItems = isMultiSelect
        ? _.isEmpty(_.get(selectedItems, "selectedAssessmentIds", null))
        : _.isEmpty(_.get(selectedItems, "selectedAssessmentId", null));
      if (hasNoSelectedItems) {
        setToastMsg({
          msg: "toastMsgs:please_select_post_to_import",
          type: "warning",
          position: "toast-bottom-left",
        });
        return;
      }
      onClickAdd();
      onClose();
    } else {
      onProgressStepClick({
        step: steps[_.findIndex(steps, { id: selectedStep }) + 1].id,
      });
    }
  };

  const onLeftButtonClick = () => {
    onProgressStepClick({
      step: steps[_.findIndex(steps, { id: selectedStep }) - 1].id,
    });
  };

  return (
    <UIModal
      modalContent={classes.modalContent}
      isOpen={true}
      isFullScreen={true}
      onRequestClose={onClose}
    >
      <TopHeader
        testId={`${testId}-assessmentImportModal`}
        headerText={
          <div className={classes.headerTextContainer}>
            <span className={"heading-4"}>{header}</span>
            {courseName && (
              <TruncateWithTooltip
                className={classNames("body-2", classes.headerSubtitle)}
                tooltip={courseName}
              >
                {courseName}
              </TruncateWithTooltip>
            )}
          </div>
        }
        onBack={onClose}
      />
      <div className={classes.progressBarContainer}>
        <CustomProgressSteps
          steps={steps}
          currentStep={selectedStep}
          testId={`${testId}-assessmentImportModal`}
          borderWidth={4}
          shouldDisplayLabel={true}
          onClick={onProgressStepClick}
        />
      </div>
      <div className={classes.stepContainer}>{renderCurrentStep()}</div>
      <div
        className={classNames(classes.footerContainer, {
          [classes.leFooterButtons]: selectedStep === "LeSelection",
        })}
      >
        <div>
          {_.findIndex(steps, { id: selectedStep }) != 0 ? (
            <Button
              variant={"outlined-subtle"}
              onClick={onLeftButtonClick}
              testId="unitPlans-assessmentImportModal-go-back"
            >
              {t("unitPlan:go_back_to_select_a_unit")}
            </Button>
          ) : null}
        </div>
        <div>
          <Button
            variant={"progressive"}
            onClick={onRightButtonClick}
            testId={`unitPlans-assessmentImportModal-${selectedStep}`}
          >
            {_.findIndex(steps, { id: selectedStep }) == _.size(steps) - 1
              ? t(importButtonText)
              : t("common:next")}
          </Button>
        </div>
      </div>
    </UIModal>
  );
}

const mapStateToProps = (state, ownProps) => {
  const { modalType, groupType, t, unitPlanId } = ownProps;
  const steps = getStepListMemoized({ t, modalType, groupType });
  const currentCurriculumType = _.get(
    state,
    "platform.currentCurriculumProgram.type",
    null
  );
  const currentCurriculumProgramAcronym = _.get(
    state,
    "platform.currentCurriculumProgram.acronym",
    null
  );
  let portalType = "PLANNER";
  if (_.isEqual(modalType, "ADD_FROM_TODDLE_LIBRARY")) {
    switch (currentCurriculumType) {
      case CURRICULUM_TYPE_MYP:
        portalType = "MYP_SAMPLE_ORG";
        break;
      case CURRICULUM_TYPE_UBD:
        portalType = "UBD_SAMPLE_ORG";
        break;
    }
  }
  const header = getModalHeaderText({
    t,
    groupType,
    modalType,
    curriculum_type: currentCurriculumProgramAcronym,
  });
  const selectedImportUnitPlan = _.get(
    state,
    "unitPlans.selectedImportUnitPlan",
    {}
  );
  const unitId = _.get(state, "planner.unitPlanData.unitPlanId", null);
  return {
    steps,
    currentSelectedStep: _.first(steps).id,
    portalType,
    selectedImportUnitPlan,
    header,
    unitPlanId: unitPlanId ?? unitId,
  };
};

const mapActionCreators = {
  setToastMsg,
  setGlobalTrayEnabledState,
  hideIntercom,
  showIntercom,
};

AssessmentImportModal.propTypes = {
  updateUserInfoPortalType: PropTypes.func,
  importButtonText: PropTypes.string,
};

AssessmentImportModal.defaultProps = {
  updateUserInfoPortalType: () => {},
  importButtonText: "common:import",
};

export default compose(
  I18nHOC({ resource: ["common", "unitPlan"] }),
  connect(mapStateToProps, mapActionCreators)
)(AssessmentImportModal);
